import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AttendanceSplitter, SecoesDeSplitter1, SequenciaDeCaixasNoBarramento, Splitter, UnbalancedBox, VerticalBuildingType } from '../../state/network-parameters.model.generated';

@Component({
  selector: 'app-vertical-unbalanced-form',
  templateUrl: './vertical-unbalanced-form.component.html',
  styleUrls: ['./vertical-unbalanced-form.component.scss']
})
export class VerticalUnbalancedFormComponent implements OnInit {

  @Input()
  type: VerticalBuildingType;

  @Input()
  form: FormGroup;

  private privateAccessPerPon: number;
  @Input()
  set accessPerPon(value: number) {
    this.privateAccessPerPon = value;
    if (this.form.controls.verticalSections) {
      this.validateSections();
    }
  }
  get accessPerPon(): number {
    return this.privateAccessPerPon;
  }

  sections = ['', null, null];
  boxs = [];
  sectionsError = false;

  readonly allowedSplitterValues = ALLOWED_SPLITTER_VALUES;
  readonly allowedBoxValues = ALLOWED_BOX_VALUES;
  readonly unbalanced: Splitter = 2;
  readonly attendanceSplitter: AttendanceSplitter = 8;
  readonly lastBox: UnbalancedBox = '1/8';

  constructor(
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.loadSplitters();
    this.loadBoxes();
    this.form.get('verticalSections').setValue(!this.sectionsError);
  }

  loadSplitters() {

    this.type.configuration.splitter_sections
      .forEach((section, i) => {
        this.sections[i] = `1x${section.splitter}`;
      });
  }

  loadBoxes() {
    if (!this.type.configuration.bus_box_types) {
      return;
    }

    this.type.configuration.bus_box_types.forEach((box, i) => {
      if (i < this.type.configuration.bus_box_types.length - 1) {
        this.boxs[i] = box;
      }
    });
  }

  sectionsChange(sections: string[]) {
    this.type.configuration.splitter_sections =
      sections
        .filter(s => s !== null && s !== '')
        .map(s => {
          return {
            splitter: Number(s.replace('1x', '')),
            can_use_internally: false
          };
        }) as SecoesDeSplitter1;

    this.validateSections();
  }

  boxsChange(boxes: string[]) {
    this.type.configuration.bus_box_types =
      boxes.filter(s => s !== null && s !== '') as SequenciaDeCaixasNoBarramento;
    this.type.configuration.bus_box_types.push(this.lastBox);
    this.validateBoxes();
  }

  private createForm() {
    const { configuration } = this.type;

    this.form.addControl(
      'min_boxes_per_bus',
      this.fb.control(configuration.min_boxes_per_bus, [Validators.required, Validators.min(1), Validators.max(10)])
    );
    this.form.addControl(
      'max_boxes_per_bus',
      this.fb.control(configuration.max_boxes_per_bus, [Validators.required, Validators.min(1), Validators.max(10)])
    );

    this.form.addControl('verticalSections', this.fb.control(true, Validators.requiredTrue));
    this.form.addControl('boxArray', this.fb.control(true, Validators.requiredTrue));

    this.form.valueChanges.subscribe(values => {
      this.type.configuration.min_boxes_per_bus = values.min_boxes_per_bus;
      this.type.configuration.max_boxes_per_bus = values.max_boxes_per_bus;

      this.validateSections();
      this.updateBoxArray();
    });

    this.form.setValidators(MinMaxValidator);
    this.form.updateValueAndValidity();
  }

  private updateBoxArray(): void {
    if (!this.form.value.max_boxes_per_bus) {
      return;
    }

    const maxBoxes = this.form.value.max_boxes_per_bus - 1;

    if (maxBoxes === this.boxs.length) {
      return;
    }

    if (maxBoxes < this.boxs.length) {
      const index = maxBoxes;
      const length = this.boxs.length - maxBoxes;
      this.boxs.splice(index, length);
    } else {
      const length = maxBoxes - this.boxs.length;
      const boxes = new Array(length).fill('');
      this.boxs = this.boxs.concat(boxes);
    }
  }

  private validateSections(): void {
    const { configuration } = this.type;

    let multiplication = 1;

    if (configuration.splitter_sections.length) {
      multiplication = configuration.splitter_sections.reduce((value, current) => {
        if (!ALLOWED_SPLITTER_VALUES.map(this.splitterToNumber).includes(current.splitter)) {
          return 0;
        }
        if (current.splitter) {
          return value * current.splitter;
        }
        return value;
      }, multiplication);
    }

    const previousSectionsError = this.sectionsError;

    if (this.accessPerPon > 0) {
      this.sectionsError = configuration.max_boxes_per_bus * this.attendanceSplitter * multiplication !== Number(this.accessPerPon);
    } else {
      this.sectionsError = false;
    }

    if (this.sectionsError !== previousSectionsError) {
      this.form.get('verticalSections').setValue(!this.sectionsError);
    }
  }

  private validateBoxes(): void {
    const busBoxTypes = this.type.configuration.bus_box_types;
    const boxError = this.form.value.max_boxes_per_bus !== busBoxTypes.filter(b => ALLOWED_BOX_VALUES.includes(b)).length;

    this.form.get('boxArray').setValue(!boxError);
  }

  private splitterToNumber(splitter: string): number {
    const value = splitter.replace('1x', '');
    return Number(value);
  }
}


const MinMaxValidator: ValidatorFn = (fg: FormGroup) => {
  const min = fg.get('min_boxes_per_bus').value;
  const max = fg.get('max_boxes_per_bus').value;

  if (min === max) {
    return;
  }
  if (!min) {
    return null;
  }
  if (!max) {
    return null;
  }
  if (min < max) {
    return null;
  }
  return { minMax: true };
};

const ALLOWED_SPLITTER_VALUES = ['1x0', '1x2', '1x4', '1x8', '1x16', '1x32'];

const ALLOWED_BOX_VALUES = ['10/90', '15/85', '20/80', '25/75', '30/70', '35/65', '40/60', '1/8'];
